import React from 'react'
import maplibregl from 'maplibre-gl'
import { useGlobalState } from 'state'
import * as api from 'common/api'
import { TimeSeriesRow } from 'common/types'
import { KouzuiKikikuruTileLayer } from 'components/layers/KouzuiKikikuruLayer/KouzuiKikikuruTileLayer'
import { KouzuiKikikuruWideLayer } from 'components/layers/KouzuiKikikuruLayer/KouzuiKikikuruWideLayer'

type Props = {
  sliderIndex: number
}

export const KouzuiKikikuruLayer = ({ sliderIndex }: Props) => {
  const state = useGlobalState()
  const [currentTileBounds, setCurrentTileBounds] = React.useState<maplibregl.LngLatBounds | null>(null)
  const [kikikuruTiles, setKikikuruTiles] = React.useState<{code: string; bounds: maplibregl.LngLatBounds}[]>([])
  const [timeSeries, setTimeSeries] = React.useState<TimeSeriesRow[]>([])
  const opacity = state.map.layerOpacity['kouzui']

  const isTileLayerZoom: boolean = React.useMemo(() => state.map.intZoom >= 9, [state.map.intZoom])

  const currentRainRow = React.useMemo((): TimeSeriesRow | null => {
    return timeSeries.length === 0 || timeSeries.length <= sliderIndex || sliderIndex < 0 ? null : timeSeries[sliderIndex]
  }, [timeSeries, sliderIndex])

  const tileRainRow = React.useMemo((): TimeSeriesRow | null => {
    return currentRainRow && isTileLayerZoom && currentRainRow.tileUrl ? currentRainRow : null
  }, [currentRainRow, isTileLayerZoom])

  const wideRainRow = React.useMemo((): TimeSeriesRow | null => {
    return currentRainRow && isTileLayerZoom && currentRainRow.tileUrl ? null : currentRainRow
  }, [currentRainRow, isTileLayerZoom])

  React.useEffect(() => {
    if (state.mapbox) {
      fetchKikikuru()
    }
  }, [state.mapbox, state.app.baseDate])

  React.useEffect(() => {
    if (!state.mapbox) return
    if (!isTileLayerZoom) return
    if (currentTileBounds !== null) {
      if (
        currentTileBounds.contains(state.map.bounds.getNorthEast()) &&
        currentTileBounds.contains(state.map.bounds.getSouthWest())
      ) {
        return
      }
    }
    fetchKikikuru()
  }, [state.map.bounds])

  const fetchKikikuru = async () => {
    if (!state.mapbox) return
    const northwest = state.mapbox.getBounds().getNorthWest()
    const southeast = state.mapbox.getBounds().getSouthEast()
    const params = {
      northwest_lat: northwest.lat,
      northwest_lng: northwest.lng,
      southeast_lat: southeast.lat,
      southeast_lng: southeast.lng,
      date: state.app.getFormattedBaseDate(5)
    }
    const res = await api.get('/inundation-kikikuru/flood', params)
    const tiles: {
      code: string
      bounds: number[][]
    }[] = res.tiles

    if (tiles.length === 0 || !isTileLayerZoom) {
      setCurrentTileBounds(null)
      setKikikuruTiles([])
    } else {
      // ロードしているタイル境界を保持
      const north = tiles.map(t => t.bounds[0][0])
      const west = tiles.map(t => t.bounds[0][1])
      const south = tiles.map(t => t.bounds[1][0])
      const east = tiles.map(t => t.bounds[1][1])
      north.push(northwest.lat)
      west.push(northwest.lng)
      south.push(southeast.lat)
      east.push(southeast.lng)
      const sw = new maplibregl.LngLat(
        Math.min(...west),
        Math.min(...south)
      )
      const ne = new maplibregl.LngLat(
        Math.max(...east),
        Math.max(...north)
      )
      const bounds = new maplibregl.LngLatBounds(sw, ne)
      setCurrentTileBounds(bounds)

      // 表示するタイルデータ
      const convertTiles = tiles.map(tile => {
        const sw = new maplibregl.LngLat(tile.bounds[0][1], tile.bounds[1][0])
        const ne = new maplibregl.LngLat(tile.bounds[1][1], tile.bounds[0][0])
        return {
          code: tile.code,
          bounds: new maplibregl.LngLatBounds(sw, ne)
        }
      })
      setKikikuruTiles(convertTiles)
    }

    // スライダー 時系列
    setTimeSeries(res.timeSeries)
  }

  return (
    <>
      { tileRainRow && kikikuruTiles.map(tile => <KouzuiKikikuruTileLayer key={tile.code} code={tile.code} bounds={tile.bounds} rain={tileRainRow} opacity={opacity} />) }
      <KouzuiKikikuruWideLayer rain={wideRainRow} opacity={opacity}  />
    </>
  )
}
